import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Cloud, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ToolIcon } from "@/components/iconMap";
import { AdUnit } from "@/components/AdUnit";
import { SeoSections } from "@/components/SeoSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AI_TOOLS, AI_TOOL_LIST } from "@/data/aiTools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function copy(text) {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard!"));
}

function ResultList({ items }) {
  return (
    <ul className="space-y-2" data-testid="ai-result-list">
      {items.map((it, i) => (
        <li key={i} className="flex items-start justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
          <span className="text-sm text-slate-800">{it}</span>
          <button onClick={() => copy(it)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 shrink-0" data-testid={`copy-item-${i}`} aria-label="Copy">
            <Copy className="w-4 h-4" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function AiToolPage() {
  const { slug } = useParams();
  const cfg = AI_TOOLS[slug];
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!cfg) return <Navigate to="/ai-tools" replace />;

  const set = (k, v) => setValues((o) => ({ ...o, [k]: v }));

  const submit = async () => {
    const required = cfg.fields.find((f) => f.required);
    if (required && !values[required.name]?.trim()) { toast.error("Please describe your product first."); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}${cfg.endpoint.replace("/api", "")}`, values);
      setResult(data);
      toast.success("Generated!");
    } catch (e) {
      const msg = e.response?.data?.detail || "The AI service could not generate a result. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const others = AI_TOOL_LIST.filter((t) => t.slug !== cfg.slug).slice(0, 3);

  return (
    <>
      <Seo title={cfg.metaTitle} description={cfg.metaDesc} path={cfg.path} faq={cfg.faq} jsonLd={cfg.jsonLd}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "AI Listing Tools", path: "/ai-tools" }, { name: cfg.name, path: cfg.path }]} />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-indigo-600/25 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="w-3 h-3" />
            <Link to="/ai-tools" className="hover:text-white">AI Listing Tools</Link><ChevronRight className="w-3 h-3" />
            <span className="text-slate-200">{cfg.name}</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
              <ToolIcon name={cfg.icon} className="w-7 h-7 text-indigo-300" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{cfg.name}</h1>
              <p className="mt-3 text-lg text-slate-300 max-w-2xl leading-relaxed">{cfg.promise}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative -mt-6 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 shadow-xl shadow-slate-200/50" data-testid="ai-widget">
            <div className="space-y-4">
              {cfg.fields.map((f) => (
                <div key={f.name}>
                  <Label className="text-sm font-semibold text-slate-800">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} maxLength={600} className="mt-1.5" data-testid={`ai-field-${f.name}`} />
                  ) : f.type === "select" ? (
                    <Select value={values[f.name] || f.options[0]} onValueChange={(v) => set(f.name, v)}>
                      <SelectTrigger className="mt-1.5" data-testid={`ai-field-${f.name}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} maxLength={300} className="mt-1.5" data-testid={`ai-field-${f.name}`} />
                  )}
                </div>
              ))}
            </div>

            <Button onClick={submit} disabled={loading} className="w-full mt-6 h-12 text-base bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/25" data-testid="ai-generate-button">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating…</> : <><Sparkles className="w-5 h-5 mr-2" /> Generate</>}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-3 inline-flex items-center justify-center gap-1.5 w-full">
              <Cloud className="w-3.5 h-3.5 text-indigo-500" />
              The text you enter is sent to an AI service to generate your result.
            </p>

            {result && (
              <div className="mt-6 pt-6 border-t border-slate-200 animate-float-up" data-testid="ai-result">
                {cfg.resultType === "list" && <ResultList items={result[cfg.resultKey] || []} />}
                {cfg.resultType === "text" && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="flex justify-end mb-2">
                      <button onClick={() => copy(result.description)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600" data-testid="copy-description"><Copy className="w-3.5 h-3.5" /> Copy</button>
                    </div>
                    <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{result.description}</p>
                  </div>
                )}
                {cfg.resultType === "listing" && (
                  <div className="space-y-5">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center justify-between">Title <button onClick={() => copy(result.title)} className="text-xs text-blue-600 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button></h4>
                      <p className="text-sm bg-slate-50 border border-slate-200 rounded-xl p-3 text-slate-800">{result.title}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center justify-between">Description <button onClick={() => copy(result.description)} className="text-xs text-blue-600 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button></h4>
                      <p className="text-sm bg-slate-50 border border-slate-200 rounded-xl p-3 text-slate-800 whitespace-pre-wrap">{result.description}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-2">Bullet points</h4>
                      <ResultList items={result.bullets || []} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center justify-between">Keywords <button onClick={() => copy((result.keywords || []).join(", "))} className="text-xs text-blue-600 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy all</button></h4>
                      <div className="flex flex-wrap gap-2">
                        {(result.keywords || []).map((k, i) => <span key={i} className="bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-3 py-1 text-xs font-medium">{k}</span>)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <AdUnit />

          <div className="mt-10">
            <h2 className="text-xl font-bold text-slate-900 mb-4">More AI listing tools</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {others.map((t) => (
                <Link key={t.path} to={t.path} className="group bg-white border border-slate-200 hover:border-indigo-400 rounded-xl p-5 transition-colors" data-testid={`ai-related-${t.slug}`}>
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    <ToolIcon name={t.icon} className="w-5 h-5" />
                  </div>
                  <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {cfg.content && (
        <SeoSections
          accent="indigo"
          intro={cfg.content.intro}
          howTo={cfg.content.howTo}
          faq={cfg.faq}
          links={cfg.content.links}
        />
      )}
    </>
  );
}
